// ProduccionEstimacionListado.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { RefreshCw, X, Search, FileSpreadsheet } from "lucide-react";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Select } from "./components/ui/select";
import { DataTable } from "./components/ui/data-table";
import { DateRangePicker } from "./components/ui/date-range-picker";
import { toast } from "./lib/toast";
import apiUrl from "./config/config";

const token =
  "Bearer 0j2whpl3*s+2/j-hCl!!-5A-8Corubp5[gqzhGrA.b*hBdO[+r.yGIFpvpOqv";

const EP_PRODUCTORES = `${apiUrl}/ServiceFamilyGrowers.asmx/GetFamilyProductores`;
const EP_VARIEDADES  = `${apiUrl}/ServiceFamilyGrowers.asmx/GetFamilyVariedad`;
const EP_LISTADO     = `${apiUrl}/ServiceFamilyGrowers.asmx/FamilyGetProduccionEstimaciones`;

const parseResponse = (data) => {
  try {
    if (data && typeof data === "object") {
      if ("d" in data) return typeof data.d === "string" ? JSON.parse(data.d) : data.d;
      return data;
    }
    let text = String(data ?? "");
    let cleaned = text.replace(/\{\s*"d"\s*:\s*null\s*\}\s*$/i, "").replace(/,\s*([}\]])/g, "$1");
    const s = cleaned.indexOf("{"); const e = cleaned.lastIndexOf("}");
    if (s !== -1 && e !== -1 && e > s) cleaned = cleaned.slice(s, e + 1);
    return JSON.parse(cleaned);
  } catch { return null; }
};

const trimStr = (v) => (typeof v === "string" ? v.trim() : v);

const ProduccionEstimacionListado = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hydrated, setHydrated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowsRaw, setRowsRaw] = useState([]);
  const [range, setRange] = useState([dayjs().subtract(30, "day"), dayjs()]);
  const [idProductor, setIdProductor] = useState(0);
  const [idVariedad, setIdVariedad] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [searchTextDebounced, setSearchTextDebounced] = useState("");
  const [loadingProd, setLoadingProd] = useState(false);
  const [productores, setProductores] = useState([{ value: 0, label: "Todos" }]);
  const [loadingVar, setLoadingVar] = useState(false);
  const [variedades, setVariedades] = useState([{ value: 0, label: "Todas" }]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  useEffect(() => {
    const t = setTimeout(() => setSearchTextDebounced(searchText.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [searchText]);

  const normalize = (arr) =>
    (Array.isArray(arr) ? arr : []).map((r, i) => {
      const productor = trimStr(r.productor);
      const variedad = trimStr(r.variedad);
      const cuartel = trimStr(r.cuartel);
      const fecha = r.fechaRegistro ? dayjs(r.fechaRegistro).format("YYYY-MM-DD") : "";
      const kilos = r.kilosEstimados != null ? Number(r.kilosEstimados) : null;
      const haystack = [productor ?? "", variedad ?? "", cuartel ?? "", fecha ?? ""].join(" ").toLowerCase();
      return { key: r.idEstimacion ?? r.id ?? i, id: r.idEstimacion ?? r.id ?? i, fecha, productor, variedad, cuartel, kilosEstimados: kilos, idProductor: Number(r.idProductor ?? 0), idVariedad: Number(r.idVariedad ?? 0), _haystack: haystack };
    });

  const fetchListado = useCallback(async () => {
    try {
      if (!range || range.length !== 2) { toast.error("Selecciona un rango de fechas."); return; }
      setLoading(true);
      const res = await axios.get(EP_LISTADO, {
        headers: { Authorization: token },
        params: { fechaDesde: dayjs(range[0]).format("YYYY-MM-DD"), fechaHasta: dayjs(range[1]).format("YYYY-MM-DD"), idProductor: Number(idProductor || 0), idVariedad: Number(idVariedad || 0), q: searchText || "" },
      });
      const payload = parseResponse(res.data) ?? (res.data?.d ? JSON.parse(res.data.d) : res.data);
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      setRowsRaw(normalize(list));
      setPagination((p) => ({ ...p, current: 1 }));
    } catch (error) {
      console.error(error);
      toast.error("Error al consultar estimaciones de producción.");
    } finally {
      setLoading(false);
    }
  }, [range, idProductor, idVariedad, searchText]);

  const fetchProductores = useCallback(async () => {
    try {
      setLoadingProd(true);
      const res = await axios.get(EP_PRODUCTORES, { headers: { Authorization: token } });
      const payload = parseResponse(res.data) ?? (res.data?.d ? JSON.parse(res.data.d) : res.data);
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      setProductores([{ value: 0, label: "Todos" }].concat(list.map((x) => ({ value: Number(x.id || x.idproductor || 0), label: String(x.nombre || "").trim() }))));
    } catch (e) { console.error(e); toast.error("No se pudieron cargar los productores."); }
    finally { setLoadingProd(false); }
  }, []);

  const fetchVariedades = useCallback(async () => {
    try {
      setLoadingVar(true);
      const res = await axios.get(EP_VARIEDADES, { headers: { Authorization: token } });
      const payload = parseResponse(res.data) ?? (res.data?.d ? JSON.parse(res.data.d) : res.data);
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      setVariedades([{ value: 0, label: "Todas" }].concat(list.map((x) => ({ value: Number(x.id || 0), label: String(x.nombre || "").trim() }))));
    } catch (e) { console.error(e); toast.error("No se pudieron cargar las variedades."); }
    finally { setLoadingVar(false); }
  }, []);

  const filteredRows = useMemo(() => {
    if (!searchTextDebounced) return rowsRaw;
    return rowsRaw.filter((r) => r._haystack?.includes(searchTextDebounced));
  }, [rowsRaw, searchTextDebounced]);

  useEffect(() => {
    const d1 = searchParams.get("d1"); const d2 = searchParams.get("d2");
    const pid = Number(searchParams.get("prod") || 0); const vid = Number(searchParams.get("var") || 0);
    const q = searchParams.get("q") || ""; const pg = Number(searchParams.get("pg") || 1); const ps = Number(searchParams.get("ps") || 20);
    if (d1 && d2) setRange([dayjs(d1), dayjs(d2)]);
    setIdProductor(pid); setIdVariedad(vid); setSearchText(q);
    setPagination({ current: pg || 1, pageSize: ps || 20 });
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    fetchProductores(); fetchVariedades(); fetchListado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  const columns = useMemo(() => [
    { title: "Fecha", dataIndex: "fecha", width: 120 },
    { title: "Productor", dataIndex: "productor", ellipsis: true, width: 230 },
    { title: "Variedad", dataIndex: "variedad", ellipsis: true, width: 170 },
    { title: "Cuartel", dataIndex: "cuartel", ellipsis: true, width: 160 },
    {
      title: "Kilos Estimados", dataIndex: "kilosEstimados", align: "right", width: 140,
      render: (v) => v == null ? "—" : v.toLocaleString("es-CL", { minimumFractionDigits: 0 }),
      sorter: (a, b) => (a.kilosEstimados || 0) - (b.kilosEstimados || 0),
    },
  ], []);

  const handleExportExcel = useCallback(async () => {
    try {
      const XLSX = await import("xlsx");
      const data = filteredRows.map((r) => ({ ID: r.id, Fecha: r.fecha, Productor: r.productor, Variedad: r.variedad, Cuartel: r.cuartel, "Kilos Estimados": r.kilosEstimados != null ? Number(r.kilosEstimados) : "" }));
      const ws = XLSX.utils.json_to_sheet(data);
      const headers = Object.keys(data[0] || {});
      ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length, ...data.map((row) => (row[h] ? String(row[h]).length : 0))) + 2 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Estimaciones");
      XLSX.writeFile(wb, `ProduccionEstimaciones_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`);
    } catch (e) { console.error(e); toast.error("No se pudo exportar a Excel."); }
  }, [filteredRows]);

  const handleLimpiar = () => {
    setRange([dayjs().subtract(30, "day"), dayjs()]);
    setIdProductor(0); setIdVariedad(0); setSearchText(""); setRowsRaw([]);
    setPagination({ current: 1, pageSize: 20 });
    setSearchParams({}, { replace: true });
  };

  return (
    <div className="bg-white p-4 rounded-xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900 m-0">Estimado de Producción</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Buscar por cuartel / productor / variedad"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 w-72"
            />
            {searchText && (
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearchText("")}>
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
          <DateRangePicker
            value={range}
            onChange={(vals) => setRange(vals || [null, null])}
            format="YYYY-MM-DD"
            allowClear={false}
          />
          <Select
            loading={loadingProd}
            options={productores}
            value={idProductor}
            onChange={(v) => setIdProductor(Number(v) || 0)}
            placeholder="Selecciona productor"
            style={{ width: 260 }}
            showSearch
            allowClear={false}
            optionFilterProp="label"
          />
          <Select
            loading={loadingVar}
            options={variedades}
            value={idVariedad}
            onChange={(v) => setIdVariedad(Number(v) || 0)}
            placeholder="Selecciona variedad"
            style={{ width: 240 }}
            showSearch
            allowClear={false}
            optionFilterProp="label"
          />
          <Button onClick={fetchListado} loading={loading}>
            <RefreshCw className="h-4 w-4" /> Buscar
          </Button>
          <Button variant="outline" onClick={handleLimpiar}>
            <X className="h-4 w-4" /> Limpiar
          </Button>
          <Button variant="outline" onClick={handleExportExcel}>
            <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
          </Button>
        </div>
      </div>

      <DataTable
        loading={loading}
        columns={columns}
        data={filteredRows}
        rowKey="id"
        bordered
        scroll={{ x: 1100 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, [start, end]) => `${start}-${end} de ${total} registros`,
        }}
        onPaginationChange={(page, pageSize) => setPagination({ current: page, pageSize })}
      />
    </div>
  );
};

export default ProduccionEstimacionListado;
